// QualityCheck/useQcQueue.ts — SKIN-239
import { useState, useEffect } from 'react';
import axios from 'axios';
import { qualityCheckServices } from '../../../services/qualityCheck';
import { QcQueueRow } from './VisitQueue';

/**
 * Fetches the QC review queue for an investigation, once per investigation.
 *
 * The state filter is NOT a parameter here: the endpoint returns every reviewable visit
 * with its state on each row, so filtering is a local narrowing in VisitQueue rather
 * than a round trip. `retry` is therefore the only refresh path, and the queue surfaces
 * it as a control rather than reserving it for the error state.
 */
export const useQcQueue = (investigation: { uuid: string } | undefined) => {
    const [data, setData] = useState<QcQueueRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [reloadToken, setReloadToken] = useState(0);

    const retry = () => setReloadToken(t => t + 1);

    useEffect(() => {
        // Cancels the XHR for real and marks this run superseded, so a response for a
        // previous investigation, or for an unmounted screen, cannot land in current state.
        // Switching investigation from the sidebar is the real case: without this, one
        // study's visits can render under another's name. Mechanism note in the service.
        const source = axios.CancelToken.source();
        const superseded = () => Boolean(source.token.reason);

        const fetchData = async () => {
            // No investigation to query: either it has not loaded yet, or the caller
            // withheld it because the visitor is not authorized. Both are idle, not
            // in-flight — returning while `loading` stays true strands a skeleton on
            // screen forever, since nothing else ever clears it.
            if (!investigation?.uuid) {
                setLoading(false);
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const rows = await qualityCheckServices.getQcQueue(investigation.uuid, source.token);
                if (superseded()) return;
                setData(rows);
            } catch (err) {
                // A cancellation is not a failure; retry() and unmount both cancel.
                if (axios.isCancel(err) || superseded()) return;
                setError(err instanceof Error ? err : new Error('Unknown error'));
            } finally {
                if (!superseded()) setLoading(false);
            }
        };

        fetchData();
        return () => source.cancel('superseded');
    }, [investigation?.uuid, reloadToken]);

    return { data, loading, error, retry };
};
